import React, { useState, useEffect } from 'react';
import firebaseAuthService from '../services/firebaseAuthService';
import translationService from '../services/translationService';

const TaskForm = ({ task, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    assignedTo: '',
    dueDate: '',
    priority: 'Medium',
    status: 'Pending'
  });
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  const { t } = translationService.useTranslation();

  useEffect(() => {
    // If editing an existing task, populate the form
    if (task) {
      setFormData(task);
    }
    
    // Load available workers
    loadAvailableWorkers();
  }, [task]);

  const loadAvailableWorkers = async () => {
    try {
      const currentSession = firebaseAuthService.getCurrentSession();
      if (!currentSession?.farm?.id) {
        setLoading(false);
        return;
      }

      // Get all farm users (workers and managers)
      const farmUsers = await firebaseAuthService.getFarmUsers(currentSession.farm.id);
      
      // Filter for workers and managers (managers can also be assigned tasks)
      const workers = farmUsers
        .filter(user => user.role === 'worker' || user.role === 'manager')
        .map(user => ({
          id: user.uid,
          name: user.name,
          role: user.role
        }))
        .sort((a, b) => {
          // Sort managers first, then workers
          if (a.role === 'manager' && b.role === 'worker') return -1;
          if (a.role === 'worker' && b.role === 'manager') return 1;
          return a.name.localeCompare(b.name);
        });

      setAvailableWorkers(workers);
    } catch (error) {
      console.error('Error loading workers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      id: task ? task.id : Date.now().toString() // Use existing ID or create new one
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="font-semibold text-lg mb-4">{task ? 'Edit Task' : 'Add New Task'}</h3>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Task Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('assignedTo')}
          </label>
          {loading ? (
            <div className="w-full p-2 border border-gray-300 rounded bg-gray-50">
              {t('loadingWorkers')}...
            </div>
          ) : availableWorkers.length > 0 ? (
            <select
              name="assignedTo"
              value={formData.assignedTo}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            >
              <option value="">{t('selectWorker')}</option>
              {availableWorkers.map(worker => (
                <option key={worker.id} value={worker.name}>
                  {worker.name} ({t(worker.role)})
                </option>
              ))}
            </select>
          ) : (
            <div className="w-full p-2 border border-gray-300 rounded bg-yellow-50 text-yellow-800">
              {t('noWorkersAvailable')}. {t('addWorkersInUserManagement')}.
            </div>
          )}
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
          <input
            type="date"
            name="dueDate"
            value={formData.dueDate}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
            required
          />
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
        
        <div className="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded text-white"
          >
            {task ? 'Update Task' : 'Create Task'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TaskForm; 